import { BCMSDate, BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, ModalOpen, StateManager, useBCMSContext, useParams, useRoute, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import FormContainer, { FormButtonContainer, FormRow } from 'components/shared/containers/FormContainer';
import CustomSelect, { CustomSelectItem } from 'components/ui/CustomSelect';
import UIAmountInput from 'components/ui/UIAmountInput';
import { UICancelButton, UIPrimaryButton } from 'components/ui/UIButton';
import UIExplanationTextInput from 'components/ui/UITextInput/components/UIExplanationTextInput';
import Classes, { Transaction } from 'model';
import React from 'react';
import DisputeProgressWidget from '../widgets/DisputeProgressWidget';
import "./styles.css";

/**
 * @function DisputeAmountView
 * @return {React.Component}
 * @exported true
 * @description The View that asks how much of the amount is being disputed.
 */

export default function DisputeAmountView(props){
  const bcmsContext = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, bcmsContext);
  const navigate = useRoute();
  const params = useParams();
  const { goStep, nextStep, prevStep, state } = useWorkflow() ?? {};
  const paramsTransactionID = useParams().transactionID ?? "0";
  const data = { ...getDefaultData(paramsTransactionID), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };
  const defaultDispute = BCMSDefaultModel.transactionDispute();

  const controller = (state, setState) => ({
    data: state.data,
    ui: state.ui,

    update: type => (val, label) => {
      let reason = label.toLowerCase();
      const crud = BCMSStateManagerCRUD.init(state);
      
      crud
        .update([ "data", "dispute", type ], reason)
        .update([ "ui" ], ui)
        .submit(setState);
    },

    updateCustomSelect: type => val => {
      let reason = val.toLowerCase();
      const crud = BCMSStateManagerCRUD.init(state);

      if(reason === "full") {
        crud
        .update([ "data", "dispute", "partialDisputeAmount", "amount" ], defaultDispute.partialDisputeAmount)
        .update([ "data", "dispute", "whyAreYouNotDisputingTheFullAmount"], "");
      }
      
      crud
        .update([ "data", "dispute", type ], reason)
        .update([ "ui" ], ui)
        .submit(setState);
    },

    // User might select a date from a calendar or type a date.
    updateCustomCalendar: type => val => {
      const crud = BCMSStateManagerCRUD.init(state);

      if(val instanceof BCMSDate) {
        crud.update([ "data", "dispute", type ], val)
      } else {
        let validatedTypeDate = new Date(val);
        let typedDateIsValid = validatedTypeDate.toString() !== "Invalid Date";

        if(typedDateIsValid) {
          validatedTypeDate = BCMSDate.init(validatedTypeDate);
        } else {
          validatedTypeDate = BCMSDate.init(0);
        }

        crud.update([ "data", "dispute", type ], validatedTypeDate)
      }

      crud.update([ "data", "dispute", "didSelectWhenWasTheMerchandiseReturned"], true)
      crud.submit(setState);
    },

    updateTextInput: type => (arg1, arg2, arg3) => {
      BCMSStateManagerCRUD.init(state)
        .update([ "data", "dispute", type ], arg1)
        .update([ "ui" ], ui)
        .submit(setState);
    },

    next: evt => {
      goStep(5, { data, ui });
    },

    cancel: () => {
      navigate(`/home/transactiondetails/${ params.cardID }/${ params.transactionID }`);
    }
  });

  return(
    <StateManager state={ { data, ui } } controller={ controller }>
      <PageContent/>
    </StateManager>
  );
}

export const getDefaultData = transactionID => {
  const transaction = Transaction.initByID(transactionID);
  const dispute = BCMSDefaultModel.transactionDispute();
  dispute.transaction = transaction;

  return {
    dispute
  };
}

export const getDefaultUI = () => ({
  howMuchOfTheTransactionAmountAreYouDisputingSelectIsActive: false
});

function PageContent(props) {
  const { data, ui, cancel, next, updateCustomSelect } = useStateManager();
  const dispute = data.dispute ?? {};
  const nextButtonEnabledCondition1 = dispute.howMuchOfTheTransactionAmountAreYouDisputing === "full";
  const nextButtonEnabledCondition2 = dispute.howMuchOfTheTransactionAmountAreYouDisputing === "partial" 
  && dispute.partialDisputeAmount.amount
  && dispute.whyAreYouNotDisputingTheFullAmount.length > 0;
  const onCancel = evt => cancel();
  const nextButtonIsEnabled = nextButtonEnabledCondition1 || nextButtonEnabledCondition2;
  const customSelectDataPoint = { "data-point": "Disputing_Amount_Select" };
  const partialDataPoint = { "data-point": "Partial_Amount_Option" };
  const fullAmountDataPoint = { "data-point": "Full_Amount_Option" };
  const nextButtonDataPoint = { dataPoint: "Next_Button" };

  return (
    <div className="dispute-amount-view">
      <DisputeProgressWidget step="2" />
      <FormContainer>
        <FormRow>
          <p className="form-label">How much of the transaction amount are you disputing? *</p>
          <CustomSelect isActive={ ui.howMuchOfTheTransactionAmountAreYouDisputingSelectIsActive } label="Select amount" { ...customSelectDataPoint } value={ dispute.howMuchOfTheTransactionAmountAreYouDisputing }
          onChange={ updateCustomSelect("howMuchOfTheTransactionAmountAreYouDisputing")}>
            <CustomSelectItem value="partial">
              <span { ...partialDataPoint }>Partial Amount</span>
            </CustomSelectItem>
            <CustomSelectItem value="full">
              <span { ...fullAmountDataPoint }>Full Amount</span>
            </CustomSelectItem>
          </CustomSelect>
        </FormRow>

        { dispute.howMuchOfTheTransactionAmountAreYouDisputing === "partial" &&
        <PartialAmountWidget />
        }

        <FormButtonContainer>
          <UIPrimaryButton text="Next" { ...nextButtonDataPoint } className={ nextButtonIsEnabled ? "" : "disabled" } onClick={ next } />
          <ModalOpen id="CancelDisputeTransactionModal" data={ { onCancel: onCancel } }>
            <UICancelButton text="Cancel" />
          </ModalOpen>
        </FormButtonContainer>
      </FormContainer>
    </div>
  );
}

/**
 * @function PartialAmountWidget
 * @return {React.Component}
 * @exported false
 * @description The widget that asks about how much of the amount is being disputed.
 */

function PartialAmountWidget(props) {
  const { data, update, updateCustomSelect, updateTextInput } = useStateManager();
  const dispute = data.dispute ?? {};

  return(
    <div className="partial-amount-widget">
      <FormRow>
        <p className="form-label">Please provide the amount of the charge you are disputing. *</p>
        <p className="sub-text">Dispute Amount</p>
        <UIAmountInput label="Dispute amount" value={ dispute.partialDisputeAmount.amount } onChange={ updateCustomSelect("partialDisputeAmount") } />
      </FormRow>
      <FormRow>
        <p className="form-label">Why are you not disputing the full amount? *</p>
        <UIExplanationTextInput maxLength="300" value={ dispute.whyAreYouNotDisputingTheFullAmount } 
        onChange={ updateTextInput("whyAreYouNotDisputingTheFullAmount") } />
      </FormRow>
    </div>
  );
}